import { FastForwardOutlined } from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { toast } from 'react-toastify'

import CustomDialog from '~/components/customDialog'
import useAdvanceCampaignTime from '~/features/campaigns/hooks/useAdvanceCampaignTime'
import useGetAdvanceCampaignTimePreview from '~/features/campaigns/hooks/useGetAdvanceCampaignTimePreview'
import useIsAdmin from '~/features/campaigns/hooks/useIsAdmin'
import {
  ECampaignExecutionStatus,
  ECampaignKeys,
} from '~/features/campaigns/models'
import useGetSelectedCampaign from '~/hooks/useGetSelectedCampaign'
import rem from '~/utils/ui'

/** Matches the campaign service's ADVANCE_TIME_MAX_CONTACTS gate, which returns 400 above it. */
const ADVANCE_TIME_MAX_CONTACTS = 500

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

const formatCampaignCheckpoint = (isoDate: string | null | undefined) => {
  if (!isoDate) return null
  const parsed = new Date(isoDate)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

/**
 * How far the clock actually moves. Without this the button reads the same whether it buys
 * you five minutes or five days — which is the whole reason to advance.
 */
const formatJump = (
  fromIso: string | null | undefined,
  toIso: string | null,
) => {
  if (!fromIso || !toIso) return null
  const from = new Date(fromIso).getTime()
  const to = new Date(toIso).getTime()
  if (Number.isNaN(from) || Number.isNaN(to)) return null

  const deltaMs = to - from
  if (deltaMs <= MINUTE_MS) return 'less than a minute later'

  if (deltaMs < HOUR_MS) {
    const minutes = Math.round(deltaMs / MINUTE_MS)
    return `${minutes} minute${minutes === 1 ? '' : 's'} later`
  }
  if (deltaMs < DAY_MS) {
    const hours = Math.round(deltaMs / HOUR_MS)
    return `${hours} hour${hours === 1 ? '' : 's'} later`
  }
  const days = Math.round(deltaMs / DAY_MS)
  return `${days} day${days === 1 ? '' : 's'} later`
}

const formatChannels = (channels: string[]) => {
  if (channels.length === 0) return null
  if (channels.length === 1) return channels[0]
  return `${channels.slice(0, -1).join(', ')} and ${channels[channels.length - 1]}`
}

/**
 * Admin-only "advance in time" for test campaigns: runs the next scheduled outreach
 * checkpoint now instead of waiting for its EventBridge schedule. Renders nothing unless the
 * campaign is actually advanceable, so it is safe to drop into a shared toolbar.
 */
export default function CampaignAdvanceTimeAction() {
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const isAdmin = useIsAdmin()
  const { campaign, isArchived, isTest } = useGetSelectedCampaign()

  const campaignId = campaign?.id || ''

  const { mutate: advanceCampaignTime, isPending } =
    useAdvanceCampaignTime(campaignId)

  // Test campaigns run as either inProgress or inTesting; both are advanceable. A campaign
  // that has not started has nothing scheduled to advance to.
  const executionStatus = campaign?.[ECampaignKeys.executionStatus]
  const isAdvanceableExecutionStatus =
    executionStatus === ECampaignExecutionStatus.inProgress ||
    executionStatus === ECampaignExecutionStatus.inTesting

  const canAdvanceTime = Boolean(
    isAdmin && isTest && !isArchived && isAdvanceableExecutionStatus,
  )

  // Only fetched while the dialog is open — the checkpoint is only interesting at decision time.
  const {
    data: previewResponse,
    isFetching: isPreviewFetching,
    isError: isPreviewError,
  } = useGetAdvanceCampaignTimePreview(
    campaignId,
    canAdvanceTime && isDialogOpen,
  )

  // Reopening the dialog keeps the previous response cached, so `isLoading` is false and only
  // `isFetching` is true. Showing that cached value would present the checkpoint from the last
  // advance as if it were the next one — so an in-flight fetch counts as having no preview at
  // all, and the dialog shows its skeleton instead.
  const preview = isPreviewFetching ? undefined : previewResponse?.data

  // The click itself starts work — the preview fetch — so the button must show it, not just
  // the confirm step.
  const isBusy = isPending || isPreviewFetching

  // Toolbar label: the campaign query only. `useAdvanceCampaignTime` writes the new clock into
  // that cache the moment an advance is accepted, so this moves instantly and with no request.
  // Deliberately not the preview — its cached copy is from the last dialog open, i.e. from
  // before the advance, so preferring it would mask the update.
  const campaignDate = formatCampaignCheckpoint(
    campaign?.[ECampaignKeys.campaignDate] ?? null,
  )
  /** Dialog only, recomputed server-side on every open. */
  const freshCampaignDate = formatCampaignCheckpoint(preview?.campaignDate)
  const nextCheckpoint = formatCampaignCheckpoint(preview?.advancedTo)
  const jump = formatJump(
    preview?.campaignDate ?? campaign?.[ECampaignKeys.campaignDate],
    preview?.advancedTo ?? null,
  )
  const nextChannels = formatChannels(preview?.channels ?? [])
  const hasNothingToAdvance = Boolean(preview && !preview.advancedTo)

  const handleConfirm = () => {
    advanceCampaignTime(undefined, {
      onSuccess: (response) => {
        const result = response?.data
        setIsDialogOpen(false)

        if (!result?.advancedTo) {
          toast.info(
            result?.message || 'Nothing pending to advance in this campaign',
          )
          return
        }

        const checkpoint = formatCampaignCheckpoint(result.advancedTo)
        toast.success(
          `Advanced to ${checkpoint ?? 'the next step'} — ${
            result.enqueuedCount
          } contact${
            result.enqueuedCount === 1 ? '' : 's'
          } executing. Results appear in a few seconds.`,
        )
      },
      onError: () => {
        toast.error('Failed to advance campaign in time. Please try again.')
      },
    })
  }

  if (!canAdvanceTime) return null

  return (
    <>
      {/* Inline rather than stacked: this sits in a fixed-height toolbar row, so a second line
          crowds the controls above it. */}
      <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
        {/* Absent only when the campaign service predates this feature — say nothing rather
            than inventing a time. */}
        {campaignDate && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Campaign time: <strong>{campaignDate}</strong>
          </Typography>
        )}

        <Tooltip
          title={
            campaignDate
              ? `Test campaign is at ${campaignDate}. Run the next scheduled step now.`
              : 'Run the next scheduled step now instead of waiting for it.'
          }
          placement="bottom"
        >
          <Button
            variant="secondaryModern"
            startIcon={
              // Spins from the click itself: the dialog cannot say what the next step is
              // until the preview lands, and after confirming, execution runs off SQS.
              isBusy ? (
                <CircularProgress size={16} />
              ) : (
                <FastForwardOutlined sx={{ fontSize: rem(18) }} />
              )
            }
            sx={{ height: rem(32), whiteSpace: 'nowrap', flexShrink: 0 }}
            disabled={isBusy}
            onClick={() => setIsDialogOpen(true)}
          >
            Advance in time
          </Button>
        </Tooltip>
      </Stack>

      <CustomDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onCancel={() => setIsDialogOpen(false)}
        title="Advance campaign in time"
        description=""
        isDisabled={isBusy || hasNothingToAdvance}
        onConfirm={handleConfirm}
        cancelText="Cancel"
        confirmText={isPending ? 'Advancing…' : 'Advance'}
      >
        <Stack gap={1}>
          {freshCampaignDate && (
            <Typography variant="body2">
              This campaign is currently at <strong>{freshCampaignDate}</strong>
              .
            </Typography>
          )}

          {isPreviewFetching && (
            <Stack gap={0.5} sx={{ py: 1 }}>
              <Skeleton variant="text" width="70%" height={24} />
              <Skeleton variant="text" width="45%" height={20} />
            </Stack>
          )}

          {isPreviewError && (
            <Alert severity="error" sx={{ borderRadius: rem(8) }}>
              Could not load the next step. Advancing will still run whichever
              checkpoint is next.
            </Alert>
          )}

          {hasNothingToAdvance && (
            <Alert severity="info" sx={{ borderRadius: rem(8) }}>
              Nothing is pending — there is no next step to advance to.
            </Alert>
          )}

          {preview?.advancedTo && (
            <>
              <Divider />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Next step
              </Typography>
              <Typography variant="body1">
                <strong>{nextCheckpoint}</strong>
                {jump ? ` — ${jump}` : ''}
              </Typography>
              <Typography variant="body2">
                {preview.outreachCount} contact
                {preview.outreachCount === 1 ? '' : 's'} due then will be
                reached
                {nextChannels ? ` by ${nextChannels}` : ''}.
                {preview.remainingPendingCount > 0
                  ? ` ${preview.remainingPendingCount} outreach${
                      preview.remainingPendingCount === 1 ? '' : 'es'
                    } scheduled after this stay pending.`
                  : ''}
              </Typography>
              <Divider />
            </>
          )}

          <Typography variant="body2">
            The campaign clock moves to that moment and everything due by then
            runs, each outreach recorded at the time it was scheduled for — so
            campaign history keeps looking like a real multi-day run. Outreaches
            scheduled later stay pending until the clock reaches them.
          </Typography>

          <Box sx={{ mt: 1, borderRadius: rem(8), overflow: 'hidden' }}>
            <Alert severity="warning">
              <AlertTitle>This cannot be undone.</AlertTitle>
              Outreaches run through the normal execution path, so contacts that
              are opted out or otherwise ineligible are skipped and stay pending
              — exactly as they would on a real send. A test campaign only uses
              mocked channels, so nothing reaches a real contact. Available for
              test campaigns with up to {ADVANCE_TIME_MAX_CONTACTS} contacts.
            </Alert>
          </Box>
        </Stack>
      </CustomDialog>
    </>
  )
}
